/**
 * Блок «Розничные цены на основные продовольственные товары» (US-30 FR-3):
 * 29 позиций перечня DP-408 за отчётный месяц, у каждой — своя единица
 * измерения из ответа.
 *
 * Отличия от прототипа — по постановке, а не по вёрстке:
 *  - **сумма цен группы не выводится** (FR-3.4): сумма цен разных товаров
 *    экономического смысла не имеет; в заголовке группы — число позиций;
 *  - **группировка справочная** (FR-3.3): групп в выпуске нет, порядок
 *    групп и позиций задаёт ответ, экран их не пересобирает;
 *  - **единица — у позиции** (FR-3.5): выпуск объявляет одну единицу на
 *    всю таблицу («в теңге за килограмм»), что неверно для яиц и молока.
 *
 * Территориальная применимость ограничена источником (FR-3.6): по
 * областному центру цен нет вовсе — блок показывает признак
 * неприменимости, а не «нет данных» с нулями; на уровне области значения
 * помечены расчётными (среднее по районным центрам).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  PeriodCaption,
  SourceNote,
  StatNoData,
  StatNumber,
  fmt,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { cn } from '../../../lib/cn';
import type {
  TradePriceItem,
  TradePricesResponse,
} from '../../../types/analytics';

interface PriceGroup {
  key: string;
  /** null — позиции вне группировки: печатаются без заголовка группы. */
  name: string | null;
  items: TradePriceItem[];
}

/** Группы в порядке первого появления позиции: порядок задаёт ответ. */
const groupItems = (items: TradePriceItem[]): PriceGroup[] => {
  const groups: PriceGroup[] = [];
  items.forEach((item, index) => {
    const name = item.group;
    const group = groups.find((candidate) => candidate.name === name);
    if (group === undefined) {
      groups.push({ key: `${name ?? 'plain'}-${index}`, name, items: [item] });
      return;
    }
    group.items.push(item);
  });
  return groups;
};

interface TradePricesProps {
  data: TradePricesResponse | null;
  loading: boolean;
}

export const TradePrices = ({ data, loading }: TradePricesProps) => {
  const { t } = useTranslation();
  const [drillItem, setDrillItem] = useState<TradePriceItem | null>(null);

  const groups = groupItems(data?.items ?? []);
  const notApplicable = data !== null && !data.applicable;

  return (
    <Card className="shadow-card" data-testid="analytics-trade-prices">
      <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle>{t('analytics.trade.prices.title')}</CardTitle>
        {/* Свой отчётный период блока: месяц, а не период плашки (FR-3.2). */}
        <PeriodCaption
          period={data?.period ?? null}
          testId="trade-prices-period"
        />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : notApplicable ? (
          // Источник цен по территории не публикует (FR-3.6): отдельное
          // состояние, а не «нет данных» и не нули.
          <StatNoData
            text={t('analytics.trade.prices.notApplicable')}
            testId="trade-prices-not-applicable"
          />
        ) : groups.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            {/* Уровень области: значение получено усреднением по районным
                центрам и публикуемым по области не является (FR-3.6). */}
            {data !== null && data.calculated && (
              <p
                className="mb-3 text-sm leading-snug text-text-muted"
                data-testid="trade-prices-average-note"
                title={data.formula ?? undefined}
              >
                {t('analytics.trade.prices.averageNote', {
                  n: fmt(data.averaged_territories.length),
                })}
              </p>
            )}

            <div className="flex items-center gap-4 px-3 pb-2 text-sm text-text-faint">
              <span className="min-w-0 flex-1">
                {t('analytics.trade.prices.columnItem')}
              </span>
              <span className="w-20 shrink-0 text-right">
                {t('analytics.trade.prices.columnUnit')}
              </span>
              <span className="w-24 shrink-0 text-right">
                {t('analytics.trade.prices.columnPrice')}
              </span>
            </div>

            {groups.map((group) => (
              <div key={group.key} data-testid="trade-price-group">
                {group.name !== null && (
                  <div className="flex items-baseline gap-4 border-b border-surface-border bg-surface-muted/40 px-3 py-2">
                    <span className="min-w-0 flex-1 text-sm font-extrabold leading-snug">
                      {group.name}
                    </span>
                    {/* Число позиций, а не сумма их цен (FR-3.4). */}
                    <span className="shrink-0 text-sm text-text-muted">
                      {t('analytics.trade.prices.positionsCount', {
                        n: fmt(group.items.length),
                      })}
                    </span>
                  </div>
                )}
                <ul>
                  {group.items.map((item) => {
                    const drillable = item.details.length > 0;
                    return (
                      <li
                        key={item.code ?? item.name ?? ''}
                        className="border-b border-surface-border last:border-b-0"
                      >
                        {/* Drill-through активен только при наличии
                            первичных данных (FR-10.1). */}
                        <button
                          type="button"
                          disabled={!drillable}
                          onClick={() => setDrillItem(item)}
                          data-testid={`trade-price-${item.code ?? item.name ?? ''}`}
                          className={cn(
                            'flex w-full items-center gap-4 px-3 py-3 text-left text-sm',
                            drillable
                              ? 'cursor-pointer hover:bg-brand-50/60'
                              : 'cursor-default',
                          )}
                        >
                          <span className="min-w-0 flex-1 font-semibold leading-snug">
                            {item.name ?? '—'}
                          </span>
                          <span className="w-20 shrink-0 text-right text-text-muted">
                            {item.unit ?? '—'}
                          </span>
                          <StatNumber
                            value={item.price}
                            format={fmt}
                            className="w-24 shrink-0 text-right"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            <SourceNote source={data?.source ?? null} />
          </>
        )}
      </CardContent>

      {/* Первичные данные позиции — цены районных центров (FR-10.3). */}
      <Dialog
        open={drillItem !== null}
        onOpenChange={(open) => {
          if (!open) setDrillItem(null);
        }}
      >
        <DialogContent
          className="max-h-[85vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto"
          data-testid="trade-price-drill"
        >
          {drillItem !== null && (
            <>
              <DialogHeader>
                <DialogTitle className="text-left text-base leading-snug">
                  {drillItem.name ?? '—'}
                </DialogTitle>
                <p className="text-sm text-text-muted">
                  {t('analytics.trade.prices.drillHint')}
                </p>
              </DialogHeader>
              <ul>
                {drillItem.details.map((detail) => (
                  <li
                    key={detail.territory ?? ''}
                    className="flex items-baseline gap-4 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 leading-snug text-text-muted">
                      {detail.territory ?? '—'}
                    </span>
                    <StatNumber
                      value={detail.price}
                      format={fmt}
                      withUnit
                      className="shrink-0 text-right"
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
